export const state = () => ({
  classes: [],
  currentClass: null,
  classAttendance: null,
  studentAttendance: [],
  studentSummary: null,
  schoolAttendance: [],
  classStudents: [],
  availableStudents: [],
  loading: false,
  error: null
})

export const getters = {
  classes: (state) => state.classes,
  currentClass: (state) => state.currentClass,
  classAttendance: (state) => state.classAttendance,
  studentAttendance: (state) => state.studentAttendance,
  studentSummary: (state) => state.studentSummary,
  schoolAttendance: (state) => state.schoolAttendance,
  classStudents: (state) => state.classStudents,
  availableStudents: (state) => state.availableStudents,
  loading: (state) => state.loading,
  error: (state) => state.error
}

export const mutations = {
  SET_LOADING(state, loading) {
    state.loading = loading
  },
  SET_ERROR(state, error) {
    state.error = error
  },
  SET_CLASSES(state, classes) {
    console.log('SET_CLASSES mutation called with:', classes)
    console.log('Classes type:', Array.isArray(classes) ? 'Array' : typeof classes)
    console.log('Classes length:', Array.isArray(classes) ? classes.length : 'Not an array')
    // Use Vue.set for reactivity if needed, or create new array reference
    state.classes = Array.isArray(classes) ? [...classes] : []
    console.log('State classes after mutation:', state.classes)
  },
  SET_CURRENT_CLASS(state, classEntity) {
    state.currentClass = classEntity
  },
  ADD_CLASS(state, classEntity) {
    state.classes.push(classEntity)
  },
  UPDATE_CLASS(state, classEntity) {
    const index = state.classes.findIndex(c => c.id === classEntity.id)
    if (index !== -1) {
      state.classes.splice(index, 1, classEntity)
    }
  },
  REMOVE_CLASS(state, classId) {
    state.classes = state.classes.filter(c => c.id !== classId)
  },
  SET_CLASS_ATTENDANCE(state, attendance) {
    state.classAttendance = attendance
  },
  SET_STUDENT_ATTENDANCE(state, attendance) {
    state.studentAttendance = attendance
  },
  SET_STUDENT_SUMMARY(state, summary) {
    state.studentSummary = summary
  },
  SET_SCHOOL_ATTENDANCE(state, attendance) {
    state.schoolAttendance = attendance
  },
  SET_CLASS_STUDENTS(state, students) {
    state.classStudents = students
  },
  SET_AVAILABLE_STUDENTS(state, students) {
    state.availableStudents = students
  },
  ADD_CLASS_STUDENT(state, student) {
    state.classStudents.push(student)
  },
  REMOVE_CLASS_STUDENT(state, userId) {
    state.classStudents = state.classStudents.filter((s) => s.id !== userId && s.studentId !== userId)
  },
  UPDATE_CLASS_STUDENT(state, updatedStudent) {
    const index = state.classStudents.findIndex(s => s.id === updatedStudent.id)
    if (index !== -1) {
      state.classStudents.splice(index, 1, updatedStudent)
    }
  },
}

function getSchoolId(rootGetters) {
  const user = rootGetters['auth/currentUser']
  return user?.attributes?.school_id || user?.schoolId
}

export const actions = {
  // Class Management (Auth service). Optional schoolId: when provided (e.g. from user form) use it; otherwise use current user's school.
  async fetchClasses({ commit, state, rootGetters }, payload) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    const schoolId = (payload && payload.schoolId) || getSchoolId(rootGetters)
    if (!schoolId) {
      commit('SET_ERROR', 'School not found')
      commit('SET_CLASSES', [])
      return []
    }
    try {
      const response = await this.$axios.get('/auth/api/classes', { params: { schoolId } })
      const classesData = Array.isArray(response.data) ? response.data : []
      commit('SET_CLASSES', classesData)
      return classesData
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch classes'
      commit('SET_ERROR', errorMessage)
      commit('SET_CLASSES', [])
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchClass({ commit, rootGetters }, classId) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    const schoolId = getSchoolId(rootGetters)
    try {
      const response = await this.$axios.get(`/auth/api/classes/${classId}`, { params: { schoolId } })
      commit('SET_CURRENT_CLASS', response.data)
      return response.data
    } catch (error) {
      commit('SET_ERROR', error.response?.data?.message || 'Failed to fetch class')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async createClass({ commit, rootGetters }, classData) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    const schoolId = getSchoolId(rootGetters)
    const payload = { ...classData, schoolId }
    try {
      const response = await this.$axios.post('/auth/api/classes', payload)
      commit('ADD_CLASS', response.data)
      return response.data
    } catch (error) {
      commit('SET_ERROR', error.response?.data?.message || 'Failed to create class')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async updateClass({ commit, rootGetters }, { classId, classData }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    const schoolId = getSchoolId(rootGetters)
    try {
      const response = await this.$axios.put(`/auth/api/classes/${classId}`, classData, { params: { schoolId } })
      commit('UPDATE_CLASS', response.data)
      return response.data
    } catch (error) {
      commit('SET_ERROR', error.response?.data?.message || 'Failed to update class')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async deleteClass({ commit, rootGetters }, classId) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    const schoolId = getSchoolId(rootGetters)
    try {
      await this.$axios.delete(`/auth/api/classes/${classId}`, { params: { schoolId } })
      commit('REMOVE_CLASS', classId)
      return true
    } catch (error) {
      commit('SET_ERROR', error.response?.data?.message || 'Failed to delete class')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // Attendance Management
  async markBulkAttendance({ commit }, bulkRequest) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const response = await this.$axios.post('/attendance/api/attendance/bulk', bulkRequest)
      return response.data
    } catch (error) {
      commit('SET_ERROR', error.response?.data?.message || 'Failed to mark attendance')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchClassAttendance({ commit }, { classId, date }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const response = await this.$axios.get(`/attendance/api/attendance/class/${classId}`, {
        params: { date }
      })
      commit('SET_CLASS_ATTENDANCE', response.data)
      return response.data
    } catch (error) {
      commit('SET_ERROR', error.response?.data?.message || 'Failed to fetch class attendance')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  /** Fetch users by class and userType from Auth UserController (e.g. userType=STUDENT for attendance). */
  async fetchUsersByClassAndUserType({ commit, rootGetters }, { classId, userType }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    const schoolId = getSchoolId(rootGetters)
    if (!schoolId) {
      commit('SET_ERROR', 'School not found')
      return []
    }
    try {
      const params = { schoolId }
      if (userType) params.userType = userType
      const response = await this.$axios.get(`/auth/api/user/by-class/${classId}`, { params })
      return Array.isArray(response.data) ? response.data : []
    } catch (error) {
      commit('SET_ERROR', error.response?.data?.message || 'Failed to fetch users by class')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  /** Fetch only attendance entries for a class on a date (for merging with user list from Auth). */
  async fetchAttendanceByClassAndDate({ commit }, { classId, date }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const response = await this.$axios.get(`/attendance/api/attendance/class/${classId}/by-date`, {
        params: { date }
      })
      return response.data || { locked: false, entries: [] }
    } catch (error) {
      commit('SET_ERROR', error.response?.data?.message || 'Failed to fetch attendance by date')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  /** Load class attendance for the entry page: fetch students from User API (by class + userType=STUDENT), then merge with attendance by-date. */
  async loadClassAttendanceForEntry({ commit, state, rootGetters }, { classId, date }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    const schoolId = getSchoolId(rootGetters)
    if (!schoolId) {
      commit('SET_ERROR', 'School not found')
      commit('SET_LOADING', false)
      return null
    }
    try {
      const [users, byDate] = await Promise.all([
        this.$axios.get(`/auth/api/user/by-class/${classId}`, { params: { schoolId, userType: 'STUDENT' } }),
        this.$axios.get(`/attendance/api/attendance/class/${classId}/by-date`, { params: { date } })
      ])
      const userList = Array.isArray(users.data) ? users.data : []
      const { locked = false, entries = [] } = byDate.data || {}
      const entryMap = new Map(entries.map((e) => [String(e.userId), e.status]))
      const className = (state.classes || []).find((c) => c.id === classId)?.name || `Class ${classId}`
      const students = userList.map((u) => {
        const status = entryMap.get(String(u.id))
        return {
          studentId: u.id,
          studentName: [u.firstName, u.lastName].filter(Boolean).join(' ').trim() || '-',
          rollNumber: null,
          attendance: status ? { status } : null
        }
      })
      const payload = {
        classId,
        className,
        date,
        locked,
        students
      }
      commit('SET_CLASS_ATTENDANCE', payload)
      return payload
    } catch (error) {
      commit('SET_ERROR', error.response?.data?.message || 'Failed to load class attendance')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchStudentAttendance({ commit }, { studentId, startDate, endDate }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const response = await this.$axios.get(`/attendance/api/attendance/student/${studentId}`, {
        params: { startDate, endDate }
      })
      commit('SET_STUDENT_ATTENDANCE', response.data)
      return response.data
    } catch (error) {
      commit('SET_ERROR', error.response?.data?.message || 'Failed to fetch student attendance')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchStudentSummary({ commit }, { studentId, startDate, endDate }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const response = await this.$axios.get(`/attendance/api/attendance/student/${studentId}/summary`, {
        params: { startDate, endDate }
      })
      commit('SET_STUDENT_SUMMARY', response.data)
      return response.data
    } catch (error) {
      commit('SET_ERROR', error.response?.data?.message || 'Failed to fetch student summary')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchSchoolAttendanceByDate({ commit }, date) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const response = await this.$axios.get('/attendance/api/attendance/school/date', {
        params: { date }
      })
      commit('SET_SCHOOL_ATTENDANCE', response.data)
      return response.data
    } catch (error) {
      commit('SET_ERROR', error.response?.data?.message || 'Failed to fetch school attendance')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async lockAttendance({ commit }, { classId, date }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      await this.$axios.post('/attendance/api/attendance/lock', null, {
        params: { classId, date }
      })
      return true
    } catch (error) {
      commit('SET_ERROR', error.response?.data?.message || 'Failed to lock attendance')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // Users by class (Auth: GET /api/user/by-class/{classId}?schoolId=...&userType=STUDENT)
  async fetchClassStudents({ commit, rootGetters }, classId) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    const schoolId = getSchoolId(rootGetters)
    if (!schoolId) {
      commit('SET_CLASS_STUDENTS', [])
      return []
    }
    try {
      const response = await this.$axios.get(`/auth/api/user/by-class/${classId}`, {
        params: { schoolId, userType: 'STUDENT' }
      })
      const users = Array.isArray(response.data) ? response.data : []
      const mapped = users.map((u) => ({
        id: u.id,
        studentId: u.id,
        studentName: [u.firstName, u.lastName].filter(Boolean).join(' ').trim() || '-',
        rollNumber: u.rollNumber ?? null
      }))
      commit('SET_CLASS_STUDENTS', mapped)
      return mapped
    } catch (error) {
      commit('SET_ERROR', error.response?.data?.message || 'Failed to fetch class students')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchAvailableStudents({ commit, rootGetters }, { classId, searchQuery }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    const schoolId = getSchoolId(rootGetters)
    if (!schoolId) {
      commit('SET_AVAILABLE_STUDENTS', [])
      return []
    }
    try {
      const [schoolUsersRes, classUsersRes] = await Promise.all([
        this.$axios.get(`/auth/api/user/students/by-school/${schoolId}`),
        this.$axios.get(`/auth/api/user/by-class/${classId}`, { params: { schoolId, userType: 'STUDENT' } })
      ])
      const schoolUsers = Array.isArray(schoolUsersRes.data) ? schoolUsersRes.data : []
      const classUsers = Array.isArray(classUsersRes.data) ? classUsersRes.data : []
      const classIds = new Set(classUsers.map((u) => u.id))
      let available = schoolUsers.filter((s) => !classIds.has(s.id))
      if (searchQuery) {
        const query = searchQuery.toLowerCase()
        available = available.filter((student) => {
          const name = `${student.firstName || ''} ${student.lastName || ''}`.toLowerCase()
          const email = (student.email || '').toLowerCase()
          return name.includes(query) || email.includes(query)
        })
      }
      commit('SET_AVAILABLE_STUDENTS', available)
      return available
    } catch (error) {
      commit('SET_ERROR', error.response?.data?.message || 'Failed to fetch available students')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async addStudentToClass({ commit, rootGetters }, { classId, studentId, rollNumber, schoolId: explicitSchoolId }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    const schoolId = explicitSchoolId || getSchoolId(rootGetters)
    if (!schoolId) throw new Error('School not found')
    try {
      const response = await this.$axios.post(`/auth/api/user/${studentId}/assign-class`, {
        classId,
        schoolId
      })
      const u = response.data
      const added = {
        id: u.id,
        studentId: u.id,
        studentName: [u.firstName, u.lastName].filter(Boolean).join(' ').trim() || '-',
        rollNumber: null
      }
      commit('ADD_CLASS_STUDENT', added)
      return added
    } catch (error) {
      commit('SET_ERROR', error.response?.data?.message || 'Failed to add student to class')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async removeStudentFromClass({ commit, rootGetters }, payload) {
    const userId = typeof payload === 'object' && payload?.userId != null ? payload.userId : (typeof payload === 'object' && payload?.id != null ? payload.id : payload)
    const explicitSchoolId = typeof payload === 'object' ? payload.schoolId : undefined
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    const schoolId = explicitSchoolId || getSchoolId(rootGetters)
    if (!schoolId) throw new Error('School not found')
    try {
      await this.$axios.post(`/auth/api/user/${userId}/assign-class`, { classId: null, schoolId })
      commit('REMOVE_CLASS_STUDENT', userId)
      return true
    } catch (error) {
      commit('SET_ERROR', error.response?.data?.message || 'Failed to remove student from class')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async updateStudentRollNumber({ commit, rootGetters }, { mappingId, rollNumber }) {
    commit('SET_LOADING', false)
    return { mappingId, rollNumber }
  }
}

// Matches Java's Math.abs(UUID.getMostSignificantBits()) for matching attendance entries
function uuidToLong(uuidStr) {
  if (!uuidStr) return null
  const hex = String(uuidStr).replace(/-/g, '').substring(0, 16)
  if (hex.length !== 16) return null
  const bits = BigInt('0x' + hex)
  const maxSigned = 0x8000000000000000n
  const signed = bits >= maxSigned ? bits - 0x10000000000000000n : bits
  const abs = signed < 0n ? -signed : signed
  return Number(abs)
}

function convertToLong(value) {
  if (!value) return null
  if (typeof value === 'string' && value.includes('-')) {
    return uuidToLong(value)
  }
  const num = parseInt(value)
  return isNaN(num) ? null : num
}
